import { inject, Injectable, NgZone } from '@angular/core';
import { BaseService } from '@shared/core';
import * as L from 'leaflet';
import { NzModalService } from 'ng-zorro-antd/modal';
import { take } from 'rxjs';
import { ParkingChartComponent } from '../components/parking-chart.component';
import { IParkingDetail, IParkingLocation, StatusNameEnum } from '../models/parking-map';

declare global {
  interface Window {
    markerService?: MarkersService;
  }
}

@Injectable()
export class MarkersService {
  private httpService = inject(BaseService);
  private modalService = inject(NzModalService);
  private markers: Map<string, L.Marker> = new Map();
  private parkings: Partial<IParkingDetail>[] = [];
  private parkingDetailsCache: Map<string, IParkingDetail> = new Map();
  private ngZone = inject(NgZone);

  constructor() {
    if (!window.markerService) {
      window.markerService = this;
    }
  }

  getIcon(color: string): string {
    return `<svg width="23" height="23" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10.4475 10.5734H7.26587V8.19153H10.4475C10.8811 8.19153 11.2327 8.11829 11.5022 7.9718C11.7717 7.82532 11.968 7.62317 12.0911 7.36536C12.22 7.10168 12.2844 6.80579 12.2844 6.47766C12.2844 6.1261 12.22 5.8009 12.0911 5.50208C11.968 5.20325 11.7717 4.96301 11.5022 4.78137C11.2327 4.59387 10.8811 4.50012 10.4475 4.50012H8.42603V14.9152H5.34106V2.11829H10.4475C11.4729 2.11829 12.3547 2.30579 13.093 2.68079C13.8372 3.05579 14.4084 3.57141 14.8069 4.22766C15.2053 4.87805 15.4045 5.62219 15.4045 6.46008C15.4045 7.28625 15.2053 8.00696 14.8069 8.62219C14.4084 9.23743 13.8372 9.7179 13.093 10.0636C12.3547 10.4034 11.4729 10.5734 10.4475 10.5734Z" fill="${color}"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M5.34106 14.9152H8.42603V10.5734H10.4475C11.4729 10.5734 12.3547 10.4034 13.093 10.0636C13.8372 9.7179 14.4084 9.23743 14.8069 8.62219C15.2053 8.00696 15.4045 7.28625 15.4045 6.46008C15.4045 5.62219 15.2053 4.87805 14.8069 4.22766C14.4084 3.57141 13.8372 3.05579 13.093 2.68079C12.3547 2.30579 11.4729 2.11829 10.4475 2.11829H5.34106V14.9152ZM8.42603 8.19153H10.4475C10.8811 8.19153 11.2327 8.11829 11.5022 7.9718C11.7717 7.82532 11.968 7.62317 12.0911 7.36536C12.22 7.10168 12.2844 6.80579 12.2844 6.47766C12.2844 6.1261 12.22 5.8009 12.0911 5.50208C11.968 5.20325 11.7717 4.96301 11.5022 4.78137C11.2327 4.59387 10.8811 4.50012 10.4475 4.50012H8.42603V8.19153ZM13.9959 0.896193C12.9187 0.349719 11.713 0.118286 10.4475 0.118286H3.34106V16.9152H10.426V12.5734H10.4475C11.6884 12.5734 12.8696 12.3682 13.9293 11.8804L13.9357 11.8774C14.9793 11.3926 15.8586 10.6775 16.4856 9.70936C17.1196 8.73032 17.4045 7.62147 17.4045 6.46008C17.4045 5.29225 17.1227 4.18069 16.5143 3.18615C15.9055 2.18504 15.0425 1.42417 13.9959 0.896193Z" fill="white"/>
  </svg>`;
  }

  setParkings(parkings: Partial<IParkingDetail>[]): void {
    this.parkings = parkings;
  }

  createMarker(location: IParkingLocation): L.Marker {
    const percentage = location.totalSpots
      ? ((location.totalSpots - location.freeSpotsCount) / location.totalSpots) * 100
      : 0;
    const color = this.getMarkerColor(
      percentage,
      location.totalSpots ?? 0,
      location.freeSpotsCount ?? 0,
    );

    const parkingIcon = L.divIcon({
      className: 'parking-marker',
      html: this.getIcon(color),
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });

    const marker = L.marker([location.location.x, location.location.y], {
      icon: parkingIcon,
      parkingId: location.id,
    });

    marker.on('click', () => {
      const popup = marker.getPopup();
      if (popup && popup.isOpen()) {
        marker.closePopup();
        return;
      }

      this.closeAllPopups();

      const cachedDetails = this.parkingDetailsCache.get(location.id);
      if (cachedDetails) {
        marker
          .bindPopup(this.createPopupContent(cachedDetails), {
            closeButton: false,
            closeOnClick: false,
            autoClose: false,
          })
          .openPopup();
        return;
      }

      marker.bindPopup('Загрузка...').openPopup();
      this.httpService
        .get(`parkings/positioning/${location.id}`)
        .pipe(take(1))
        .subscribe({
          next: response => {
            if (response.value) {
              this.parkingDetailsCache.set(location.id, response.value);
              marker
                .bindPopup(this.createPopupContent(response.value), {
                  closeButton: false,
                  closeOnClick: false,
                  autoClose: false,
                })
                .openPopup();
            }
          },
          error: () => {
            marker.bindPopup('Не удалось загрузить данные парковки').openPopup();
          },
        });
    });

    this.markers.set(location.id, marker);
    return marker;
  }

  updateParkingOccupancy(parkingId: string, freeSpotsCount: number): void {
    const marker = this.markers.get(parkingId);
    if (marker) {
      const parking = this.parkings.find(p => p.id === parkingId);
      if (parking) {
        parking.freeSpotsCount = freeSpotsCount;

        const cachedDetails = this.parkingDetailsCache.get(parkingId);
        if (cachedDetails) {
          cachedDetails.freeSpotsCount = freeSpotsCount;
          this.parkingDetailsCache.set(parkingId, cachedDetails);
          if (marker.getPopup()) {
            marker.setPopupContent(this.createPopupContent(cachedDetails));
          }
        }

        const percentage = parking.totalSpots
          ? ((parking.totalSpots - freeSpotsCount) / parking.totalSpots) * 100
          : 0;
        const color = this.getMarkerColor(percentage, parking.totalSpots ?? 0, freeSpotsCount ?? 0);
        const newIcon = L.divIcon({
          className: 'parking-marker',
          html: this.getIcon(color),
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        });
        marker.setIcon(newIcon);
      }
    }
  }

  getMarkerColor(percentage: number, totalSpots: number, freeSpots: number): string {
    if (totalSpots === 0 && freeSpots === 0) {
      return 'gray';
    }
    if (percentage <= 50) {
      return 'var(--spots-green)';
    } else if (percentage <= 70) {
      return 'var(--spots-yellow)';
    } else {
      return 'var(--spots-red)';
    }
  }

  closeAllPopups(): void {
    this.markers.forEach(marker => {
      if (marker.getPopup()?.isOpen()) {
        marker.closePopup();
      }
    });
  }

  clearMarkers(): void {
    this.markers.clear();
    this.parkingDetailsCache.clear();
  }

  createPopupContent(info: IParkingDetail): string {
    const percentage = info.totalSpots
      ? ((info.totalSpots - info.freeSpotsCount) / info.totalSpots) * 100
      : 0;
    const status = StatusNameEnum[info.status];
    return `
      <div class="popup-container">
        <div class="popup-header">
          <h2>Зона № ${info?.name}</h2>
          <span class="status">${status}</span>
        </div>
        <p class="location">район ${info?.address ?? ''}</p>

        <div class="info-list">
          <div class="info-item">
            <span class="label">тип:</span>
            <span class="value">${info?.type?.name ?? '-'}</span>
          </div>
          <div class="info-item">
            <span class="label">категория:</span>
            <span class="value">${info?.category?.name ?? '-'}</span>
          </div>
          <div class="info-item">
            <span class="label">опция:</span>
            <span class="value">${info?.tarif ? '100 ₸ / 200 ₸' : 'бесплатно'}</span>
          </div>


          <div class="info-item">
            <span class="label">график работы:</span>
            <span class="value">${
              info?.workingHours ? info.workingHours.open + '-' + info.workingHours.close : ''
            }</span>
          </div>
           <div class="info-item">
            <span class="label">тел:</span>
            <span class="value">${info?.operator?.contactPhone ?? '-'}</span>
          </div>
        </div>
        <div class="images flex">
        <img src="assets/images/parking-image1.png" alt="image1" height="47" width="100%"/>
        <img src="assets/images/parking-image2.png" alt="image2" height="47" width="100%" class="mx-1"/>
        <img src="assets/images/parking-image3.png" alt="image3" height="47" width="100%"/>
        </div>

        <div class="divider"></div>
        <div class="occupancy">
          <span class="label flex items-center"><span class="flex">свободных мест:</span>  <span class="flex count ml-1">${
            info.freeSpotsCount ?? 0
          }/${info.totalSpots}</span></span>
          <div class="flex">

          <div class="progress-bar flex-1">
            <div class="progress" style="width: ${percentage}%"></div>
</div>
   <button class="ml-2" onclick="window.markerService.openChartModal('${info.name}')">
<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M9.59261 0.4646V9.71279H0.344421C0.902223 4.87042 4.75025 1.0224 9.59261 0.4646ZM10.8052 19.2358C6.63384 19.2358 3.18193 16.1477 2.5918 12.138H12.0178V2.71197C16.0275 3.30211 19.1157 6.75401 19.1157 10.9254C19.1157 15.5091 15.3889 19.2358 10.8052 19.2358Z" fill="#B3B3B3"/>
</svg>
  </button>
          </div>
        </div>
      </div>
    `;
  }

  openChartModal(parkingInfo: string): void {
    this.ngZone.run(() => {
      if (parkingInfo) {
        this.modalService.create<ParkingChartComponent>({
          nzTitle: `<span class="text-xl">Динамика нагрузки <span class="text-neutral-500">Зона № ${parkingInfo}</span></span>`,
          nzContent: ParkingChartComponent,
          nzData: { parkingInfo },
          nzFooter: null,
          nzWidth: 800,
        });
      }
    });
  }
}
